import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useLang } from '../context/LanguageContext';

export default function PackerReport() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', packager_number: '' });
  const [selectedPackager, setSelectedPackager] = useState(null);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to) params.append('date_to', f.date_to);
      if (f.packager_number) params.append('packager_number', f.packager_number);
      const res = await api.get(`/reports/packager-performance?${params}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { fetchReport(); };

  const handleClearFilters = () => {
    const cleared = { date_from: '', date_to: '', packager_number: '' };
    setFilters(cleared);
    setSelectedPackager(null);
    fetchReport(cleared);
  };

  const handlePackagerClick = (packager) => {
    const pn = packager.packager_number;
    if (selectedPackager === pn) {
      setSelectedPackager(null);
      const newF = { ...filters, packager_number: '' };
      setFilters(newF);
      fetchReport(newF);
    } else {
      setSelectedPackager(pn);
      const newF = { ...filters, packager_number: pn };
      setFilters(newF);
      fetchReport(newF);
    }
  };

  const getMajorColor = (pct) => {
    const p = parseFloat(pct);
    if (p > 5) return 'bg-red-100 text-red-700';
    if (p > 2) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 fade-in">📦 {t('packer_report_title')}</h1>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6 fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">{t('from_date')}</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">{t('to_date')}</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <div className="flex space-x-2">
              <button onClick={handleFilter}
                className="flex-1 py-3 px-4 rounded-xl text-white font-bold text-sm shadow-md active:shadow-sm transition-all touch-active"
                style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}>
                {t('apply')}
              </button>
              <button onClick={handleClearFilters}
                className="py-3 px-4 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm active:bg-gray-200 touch-active">
                {t('all_time')}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-5xl fruit-spin inline-block">🍊</div>
            <p className="mt-4 text-orange-600 font-semibold">{t('loading')}</p>
          </div>
        ) : data && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: t('total_boxes'), value: data.metrics.total_boxes, icon: '📦', bg: 'bg-orange-50 border-orange-200' },
                { label: t('avg_minor'), value: parseFloat(data.metrics.avg_minor_defect_percent).toFixed(2) + '%', icon: '⚡', bg: 'bg-yellow-50 border-yellow-200' },
                { label: t('avg_major'), value: parseFloat(data.metrics.avg_major_defect_percent).toFixed(2) + '%', icon: '🚨', bg: 'bg-red-50 border-red-200' },
                { label: t('avg_critical'), value: parseFloat(data.metrics.avg_critical_defect_percent).toFixed(2) + '%', icon: '💀', bg: 'bg-purple-50 border-purple-200' },
              ].map((card, i) => (
                <div key={i} className={`rounded-2xl shadow-sm p-4 border-2 ${card.bg} fade-in`} style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="text-2xl mb-1">{card.icon}</div>
                  <div className="text-xl font-extrabold text-gray-800">{card.value}</div>
                  <div className="text-xs font-semibold text-gray-500 mt-1">{card.label}</div>
                </div>
              ))}
            </div>

            {/* Packager Performance Table */}
            <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6">
              <h3 className="text-base font-bold text-gray-800 mb-3">📦 {t('packager_performance')}</h3>
              <p className="text-xs text-gray-400 mb-4">{t('packer_report_hint')}</p>
              <div className="touch-scroll">
                <table className="w-full text-sm" style={{ minWidth: '700px' }}>
                  <thead>
                    <tr className="border-b-2 border-orange-100">
                      <th className="text-left py-3 px-3 font-bold text-gray-600">{t('packager')}</th>
                      <th className="text-left py-3 px-3 font-bold text-gray-600">{t('name')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('boxes')}</th>
                      <th className="text-center py-3 px-3 font-bold text-yellow-600">{t('avg_minor')}</th>
                      <th className="text-center py-3 px-3 font-bold text-red-600">{t('avg_major')}</th>
                      <th className="text-center py-3 px-3 font-bold text-purple-600">{t('avg_critical')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('total_pct')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('rating')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.packagerStats.map((p, i) => {
                      const avgMajor = parseFloat(p.avg_major_pct);
                      const avgCritical = parseFloat(p.avg_critical_pct);
                      const avgTotal = parseFloat(p.avg_total_pct);
                      const isSelected = selectedPackager === p.packager_number;
                      return (
                        <tr key={i}
                          className={`border-b border-gray-50 cursor-pointer transition-colors touch-active ${isSelected ? 'bg-orange-100' : 'hover:bg-orange-50'}`}
                          onClick={() => handlePackagerClick(p)}>
                          <td className="py-3 px-3 font-semibold">{p.packager_number}</td>
                          <td className="py-3 px-3">{p.packager_name}</td>
                          <td className="py-3 px-3 text-center font-bold">{p.boxes_inspected}</td>
                          <td className="py-3 px-3 text-center"><span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">{parseFloat(p.avg_minor_pct).toFixed(2)}%</span></td>
                          <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getMajorColor(avgMajor)}`}>{avgMajor.toFixed(2)}%</span></td>
                          <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${avgCritical > 0 ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{avgCritical.toFixed(2)}%</span></td>
                          <td className="py-3 px-3 text-center font-bold">{avgTotal.toFixed(2)}%</td>
                          <td className="py-3 px-3 text-center text-lg">{avgCritical > 1 ? '💀' : avgTotal <= 3 ? '🌟' : avgTotal <= 5 ? '👍' : avgTotal <= 10 ? '⚠️' : '🚨'}</td>
                        </tr>
                      );
                    })}
                    {data.packagerStats.length === 0 && (
                      <tr><td colSpan="8" className="py-8 text-center text-gray-400">{t('no_inspections')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Inspections for Selected Packager */}
            {selectedPackager && data.recentInspections && data.recentInspections.length > 0 && (
              <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 fade-in">
                <h3 className="text-base font-bold text-gray-800 mb-1">
                  📋 {t('recent_inspections_for')} <span className="text-orange-600">{selectedPackager}</span>
                </h3>
                <p className="text-xs text-gray-400 mb-4">{t('all_inspectors_note')}</p>
                <div className="grid gap-3">
                  {data.recentInspections.map((insp, i) => (
                    <div key={insp.id} className="border border-gray-100 rounded-xl p-4 hover:bg-orange-50 transition-colors fade-in" style={{ animationDelay: `${i * 0.03}s` }}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="font-bold text-gray-800">{insp.product_code} - {insp.fruit_type}</div>
                          <div className="text-xs text-gray-500">
                            {t('inspector')}: <span className="font-semibold text-blue-600">{insp.inspector_name}</span>
                            <span className="mx-2">|</span>
                            {new Date(insp.created_at).toLocaleDateString()} {new Date(insp.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-center px-2">
                            <div className="text-xs text-yellow-600 font-bold">{t('minor_pct')}</div>
                            <div className="text-sm font-extrabold text-yellow-700">{parseFloat(insp.minor_defect_percent).toFixed(1)}%</div>
                          </div>
                          <div className="text-center px-2">
                            <div className="text-xs text-red-600 font-bold">{t('major_pct')}</div>
                            <div className="text-sm font-extrabold text-red-700">{parseFloat(insp.major_defect_percent).toFixed(1)}%</div>
                          </div>
                          {insp.critical_defects > 0 && (
                            <div className="px-2 py-1 rounded-lg bg-purple-100 border border-purple-300 text-center">
                              <div className="text-xs text-purple-700 font-bold">💀 {t('critical_pct')}</div>
                              <div className="text-sm font-extrabold text-purple-800">{parseFloat(insp.critical_defect_percent).toFixed(1)}%</div>
                            </div>
                          )}
                          <div className="text-center px-2">
                            <div className="text-xs text-gray-500 font-bold">{t('total_pct')}</div>
                            <div className="text-sm font-extrabold text-gray-800">{parseFloat(insp.total_defect_percent).toFixed(1)}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
